import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useAuth} from '../core/Auth'
import {UserModel} from '../core/_models'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(500, 'Maximum 500 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.email, values.password)
        saveAuth(auth)
        const user: UserModel = {
          id: 1,
          username: values.email,
          password: values.password,
          email: values.email,
          first_name: values.email.split('@')[0],
          last_name: values.email.split('@')[0],
          fullname: values.email.split('@')[0],
          roles: [],
          token: auth.token,
          providerCode: auth.provider?.providerCode,
          providerName: auth.provider?.providerName,
          providerAddress: auth.provider?.providerAddress,
          providerCity: auth.provider?.providerCity,
          providerProvince: auth.provider?.providerProvince,
          providerCountry: auth.provider?.providerCountry,
          roleId: auth.provider?.roleId,
          menuAccess: auth?.menu,
          isActive: auth.provider?.isActive || true,
        }
        console.log('Data : ', JSON.stringify(auth))

        setCurrentUser(user)
        navigate('/dashboard')
      } catch (error: any) {
        console.error(error)
        saveAuth(undefined)

        // Extract error message from response
        let errorMessage = 'The login details are incorrect'
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error?.message) {
          errorMessage = error.message
        }

        setStatus(errorMessage)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-1'>Sign In</h1>
      </div>

      {/* <div className='row g-3 mb-9'>
        <div className='col-md-6'>
          <button
            type='button'
            className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100'
            onClick={() => {
              setShowPassword(true);
            }}
          >
            Password
          </button>
        </div>

        <div className='col-md-6'>
          <button
            type='button'
            className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100'
            onClick={() => {
              setShowPassword(false);
            }}
          >
            OTP
          </button>
        </div>
      </div>
      
      <div className='separator separator-content my-14'>
        <span className='w-125px text-gray-500 fw-semibold fs-7'>Or with email</span>
      </div> */}

      {formik.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : (
        <div className='mb-10 p-8 rounded' />
      )}

      <div className='fv-row mb-8' style={{marginTop: '-75px'}}>
        <label htmlFor='email' className='form-label fs-6 fw-bolder text-dark'>
          Email
        </label>
        <input
          id='email'
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {'is-valid': formik.touched.email && !formik.errors.email}
          )}
          type='email'
          name='email'
          autoComplete='off'
          style={{height: '43px'}}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>

      <div className='fv-row mb-3 position-relative'>
        <label htmlFor='password' className='form-label fw-bolder text-dark fs-6 mb-0'>
          Password
        </label>
        <div className='position-relative'>
          <input
            id='password'
            type={showPassword ? 'text' : 'password'}
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.password && formik.errors.password},
              {'is-valid': formik.touched.password && !formik.errors.password}
            )}
            style={{paddingRight: '40px', height: '43px'}}
          />
          <span
            className='position-absolute top-50 end-0 translate-middle-y pe-3 cursor-pointer'
            onClick={() => setShowPassword(!showPassword)}
            style={{zIndex: 5}}
          >
            {showPassword ? (
              <i className='bi bi-eye-slash fs-3'></i>
            ) : (
              <i className='bi bi-eye fs-3'></i>
            )}
          </span>
        </div>
        <style>{`
            .is-valid {
              background-position: right calc(1.9em + 0.1875rem) center !important;
            }
          `}</style>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />
        <Link to='/auth/forgot-password' className='link-primary'>
          Forgot Password ?
        </Link>
      </div>

      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Continue</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>

      <div className='text-gray-500 text-center fw-semibold fs-6'>
        <a
          href='/documents/Panduan Penggunaan Provider Portal AFI Rent System 0.0.3.pdf'
          className='link-primary'
          download
        >
          Panduan Login Provider Portal
        </a>
      </div>
    </form>
  )
}
